import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStoreState } from 'easy-peasy';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import HelperMetiers360 from "../../../services/HelpersMetiers360.js";

import useInfiniteLoadingLogic from "../../../hooks/useInfiniteLogic.jsx";
import DynamicTableInfinite from "../../widgets/dynamicTableInfinite/DynamicTableInfinite.jsx";
import AuthorizationChecker from "../../../services/AuthorizationChecker.js";

import './ClientsTableComponent.scss';

import { useModal } from '../../../hooks/useModal.jsx';

const ClientsTableComponent = (props) => {
    const { clients, statsTime } = props;
    const navigate = useNavigate();
    
    const [sortedClients, setSortedClients] = useState([]);
    const [clientsToDisplay, setClientsToDisplay] = useState([]);
    const [shouldSort, setShouldSort] = useState(false);

    const clientsSlugs = useStoreState(actions => actions.actionSlugs.actionSlugsDispatcher('clients'));
    const { modalComponent } = useModal();
    const location = useLocation();

    useEffect(() => {
        setShouldSort(true);
        setSortedClients([...clients]);
    }, [clients]);

    const {hasMore, fetchNext: fetchData} = useInfiniteLoadingLogic(sortedClients, clientsToDisplay, setClientsToDisplay, 100);

    const isAllTimeStats = !AuthorizationChecker.isAdmin() || !!(statsTime == 'allTime');
    const timeLabel = AuthorizationChecker.isAdmin() ? (isAllTimeStats ? '(Toujours)' : '(30 jours)') : '';

    const getStatTimedValue = (stat) => {
        return (isAllTimeStats ? stat?.allTimeValue : stat?.oneMonthValue) ?? 0;
    }

    const wlIcons = {
        groupList : ["fas fa-folder", "groupe"],
        quizList : ["fas fa-question", "quiz"],
        subscriptionsQuizList : ["fas fa-question", "quiz"],
        subscriptionsVideoList : ["fas fa-video","video"],
        subscriptionQuizListIsRestrictive: ["fas fa-question", "quiz"],
        subscriptionVideoListIsRestrictive : ["fas fa-video","video"],
        videoList : ["fas fa-video","video"],
        videoBlackList : ["fas fa-video","video"],
        subscriptionsVideoBlackList: ["fas fa-video","video"],
    };

    const whiteListCount = (client) => {
        return (client.groupListCount ?? 0) + (client.quizListCount ?? 0)
            + (client.subscriptionsQuizListCount ?? 0) + (client.subscriptionsVideoListCount ?? 0)
            + (client.videoListCount ?? 0) + (client.subscriptionQuizListIsRestrictiveCount ?? 0)
            + (client.subscriptionVideoListIsRestrictiveCount ?? 0) + (client.videoBlackListCount ?? 0)
            + (client.subscriptionsVideoBlackListCount ?? 0)
    }

    const whiteListDisplay = (client) => <>
        {client.groupListCount > 0 && whitelistContentDisplay(client, 'groupList')}
        {client.videoListCount > 0 && whitelistContentDisplay(client, 'videoList')}
        {client.subscriptionsVideoListCount > 0 && whitelistContentDisplay(client, 'subscriptionsVideoList')}
        {client.subscriptionVideoListIsRestrictiveCount > 0 && whitelistContentDisplay(client, 'subscriptionVideoListIsRestrictive')}
        {client.videoBlackListCount > 0 && whitelistContentDisplay(client, 'videoBlackList')}
        {client.subscriptionsVideoBlackListCount > 0 && whitelistContentDisplay(client, 'subscriptionsVideoBlackList')}
        {client.quizListCount > 0 && whitelistContentDisplay(client, 'quizList')}
        {client.subscriptionsQuizListCount > 0 && whitelistContentDisplay(client, 'subscriptionsQuizList')}
        {client.subscriptionQuizListIsRestrictiveCount > 0 && whitelistContentDisplay(client, 'subscriptionQuizListIsRestrictive')}
    </>

    const whitelistContentDisplay = (client, wType) => {
        const listCountProp = `${wType}Count`;
        const isRestrictiveProp = `${wType}IsRestrictive`;
        const isSubscriptionWL = wType.includes("subscription");
        const isBlackList = wType.includes("Black");
        const [iconClass, iconLabel] = wlIcons[wType] ?? ["", ""];
        const classNameWL = `whitelistElement 
            ${isSubscriptionWL 
                ? "subscriptionswL" 
                : isBlackList && "blackList"
            } 
            ${client[isRestrictiveProp] ? "restrictiveWL" : ""}`;

        if (client[listCountProp]) {
          return (
            <OverlayTrigger
            placement="right"
            overlay={
                <Tooltip>
                    {client[listCountProp]} {iconLabel}{client[listCountProp] > 1 ? "s" : ""} en {isBlackList ? "blacklist" : "whitelist"}{client[isRestrictiveProp] || listCountProp.includes('Restrictive') ? " restrictive" : ""}{isSubscriptionWL ? " par abonnement" : "" }
                </Tooltip>
            }    
            >
                <div className={classNameWL}>
                    {(isSubscriptionWL || isBlackList) ? <i className="fas fa-exclamation-triangle" /> :  null}
                    <i className={iconClass}/>{client[listCountProp]}
                </div>
            </OverlayTrigger>
          );
        } else {
          return null;
        }
    };
    
    const contactsM360Display = (contactsM360) => {
        return contactsM360.reduce((accumulator, currentValue) => 
            <>
                {(accumulator.props.children !== undefined) && <>{accumulator},&nbsp;</>}
                <OverlayTrigger placement="top" key={currentValue.uniqueId}
                    overlay={<Tooltip>{currentValue.relationValue}</Tooltip>}>
                    <span className="contactM360">{currentValue.name}</span>
                </OverlayTrigger>
            </>
        , <></>);
    }

    const sortType = [
        {
            value : 'name', label : 'Établissement',
            test : HelperMetiers360.isArrayContainsValue(clients, "clientName"),
            method: (a,b) => HelperMetiers360.localeCompareWithNullable(a.clientName, b.clientName),
            display: (client) => client.clientName
        },
        {
            value: 'cp', label: "C.P.",
            test : HelperMetiers360.isArrayContainsValue(clients, "addressZipCode"),
            method: (a, b) =>HelperMetiers360.localeCompareWithNullable(a.addressZipCode, b.addressZipCode),
            display: (client) => client.addressZipCode

        },
        {
            value: 'city', label: "Ville",
            test : HelperMetiers360.isArrayContainsValue(clients, "addressCity"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.addressCity, b.addressCity),
            display: (client) => client.addressCity

        },
        {
            value: 'status', label: "Statut",
            test : HelperMetiers360.isArrayContainsValue(clients, "status"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.status, b.status),
            display: (client) => client.status

        },
        {
            value : 'tagOperations', label : 'Opé M360',
            test : HelperMetiers360.isArrayContainsValue(clients, "tagOperationsName"),
            method: (a, b) => HelperMetiers360.sortStringArray(a.tagOperationsName, b.tagOperationsName),
            display: (client) => client.tagOperationsName?.join(", ")
        },
        {
            value: 'subscriptionCount', label: "Nb abo.",
            test : HelperMetiers360.isArrayContainsValue(clients, "subscriptionCount"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.subscriptionCount, b.subscriptionCount),
            display: (client) => client.subscriptionCount ?? 0
        },
        {
            value : 'subscriptionEndDate', label : 'Date de fin',
            test : HelperMetiers360.isArrayContainsValue(clients, "subscriptionEndDate"),
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.subscriptionEndDate, b.subscriptionEndDate),
            display: (client) => client.subscriptionEndDate ? HelperMetiers360.getdisplayDateType(client.subscriptionEndDate, 'day') : "-"
        },
        {
            value: 'hasWhitelist', label: "Whitelists",
            test : clients.some((client) => client.groupListCount || client.quizListCount || client.subscriptionsQuizListCount || client.subscriptionsVideoListCount || client.videoListCount || client.subscriptionQuizListIsRestrictiveCount || client.subscriptionVideoListIsRestrictiveCount || client.videoBlackListCount || client.subscriptionsVideoBlackListCount),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(whiteListCount(a), whiteListCount(b)),
            display: (client) => <div className="wlList">{whiteListDisplay(client)}</div>,
            flatDisplay: (client) => whiteListCount(client)
        },
        {
            value: 'formationCount', label: "Webinaires",
            test : HelperMetiers360.isArrayContainsValue(clients, "formationsCount"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.formationsCount, b.formationsCount),
            display: (client) => client.formationsCount
        },
        {
            value: 'headsetCount', label: "Casques",
            test : HelperMetiers360.isArrayContainsValue(clients, "headsetCount"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.headsetCount, b.headsetCount),
            display: (client) => client.headsetCount
        },
        {
            value: 'webappAccessesCode', label: "Code webapp",
            test: HelperMetiers360.isArrayContainsValue(clients, "webappAccessesCode"),
            method: (a, b) => HelperMetiers360.sortStringArray(a.webappAccessesCode, b.webappAccessesCode),
            display: (client) => client.webappAccessesCode?.join() ?? '-'
        },
        {
            value: 'hasParent', label: "Enfant",
            test : HelperMetiers360.isArrayContainsValue(clients, "hasParent"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.hasParent, b.hasParent),
            display: (client) => +client.hasParent === 1 ? 'X': '-'
        },
        {
            value: 'childrenCount', label: "Nb d'enfants",
            test : HelperMetiers360.isArrayContainsValue(clients, "childrenCount"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.childrenCount, b.childrenCount),
            display: (client) => client.childrenCount
        },
        {
            value: 'loaderLastLog', label: "Dernier log (loader)",
            test : HelperMetiers360.isArrayContainsValue(clients, "loaderLastLog"),
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.loaderLastLog, b.loaderLastLog),
            display: (client) => HelperMetiers360.getdisplayDateType(client.loaderLastLog)
        },
        {
            value: 'users', label: "Utilisateur⋅rices",
            test : HelperMetiers360.isArrayContainsValue(clients, "users"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.users, b.users),
            display: (client) => client.users
        },
        {
            value: 'confirmedUser', label: "dont déjà connectés",
            test : HelperMetiers360.isArrayContainsValue(clients, "confirmedUser"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.confirmedUser, b.confirmedUser),
            display: (client) => client.confirmedUser
        },
        {
            value: 'pendingUser', label: "dont en attente",
            test : HelperMetiers360.isArrayContainsValue(clients, "pendingUser"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.pendingUser, b.pendingUser),
            display: (client) => client.pendingUser
        },
        {
            value: 'contactsM360', label: "Contacts M360",
            test : HelperMetiers360.isArrayContainsValue(clients, "contactsM360"),
            method: (a, b) => HelperMetiers360.sortStringArray(a.contactsM360?.map(c => c.name), b.contactsM360?.map(c => c.name)),
            display: (client) => client.contactsM360?.length > 0 &&  contactsM360Display(client.contactsM360),
            flatDisplay: (client) => client.contactsM360?.map(contact => contact.name)?.join(", ")
        },
        {
            value: 'categories', label: "Catégories",
            test : HelperMetiers360.isArrayContainsValue(clients, "categories"),
            method: (a, b) => HelperMetiers360.sortStringArray(a.categories, b.categories),
            display: (client) => client.categories?.join(", ")
        },
        {
            value: 'relationTypes', label: "Nature de la relation",
            test: HelperMetiers360.isArrayContainsValue(clients, "clientRelationTypes"),
            method: (a, b) => HelperMetiers360.sortStringArray(a.clientRelationTypes, b.clientRelationTypes),
            display: (client) => client.clientRelationTypes?.join(", ")
        },
        {
            value: 'headsetPlays', label: `Lectures XP casque ${timeLabel}`,
            test: HelperMetiers360.isArrayContainsValue(clients, "headsetPlays"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(getStatTimedValue(a.headsetPlays), getStatTimedValue(b.headsetPlays)),
            display: (client) => getStatTimedValue(client.headsetPlays)
        },
        {
            value: 'webapp2Plays', label: `Lectures XP webapp ${timeLabel}`,
            test: HelperMetiers360.isArrayContainsValue(clients, "webapp2Plays"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(getStatTimedValue(a.webapp2Plays), getStatTimedValue(b.webapp2Plays)),
            display: (client) => getStatTimedValue(client.webapp2Plays)
        },
        {
            value: 'webapp2UsersCount', label: `Utilisateur⋅rices webapp ${timeLabel}`,
            test: HelperMetiers360.isArrayContainsValue(clients, "webapp2UsersCount"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(getStatTimedValue(a.webapp2UsersCount), getStatTimedValue(b.webapp2UsersCount)),
            display: (client) => getStatTimedValue(client.webapp2UsersCount)
        },
        {
            value: 'webapp2Sessions', label: `Connexions webapp ${timeLabel}`,
            test: HelperMetiers360.isArrayContainsValue(clients, "webapp2Sessions"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(getStatTimedValue(a.webapp2Sessions), getStatTimedValue(b.webapp2Sessions)),
            display: (client) => getStatTimedValue(client.webapp2Sessions)
        },
        {
            value: 'webapp2ActivityLogs', label: `Activités webapp terminées ${timeLabel}`,
            test: HelperMetiers360.isArrayContainsValue(clients, "webapp2ActivityLogs"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(getStatTimedValue(a.webapp2ActivityLogs), getStatTimedValue(b.webapp2ActivityLogs)),
            display: (client) => getStatTimedValue(client.webapp2ActivityLogs)
        },
        {
            value: 'webapp2Stories', label: `Lectures stories webapp ${timeLabel}`,
            test: HelperMetiers360.isArrayContainsValue(clients, "webapp2Stories"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(getStatTimedValue(a.webapp2Stories), getStatTimedValue(b.webapp2Stories)),
            display: (client) => getStatTimedValue(client.webapp2Stories)
        }
    ];

    const onSelectClient = (uniqueID) => {
        navigate(clientsSlugs.readOne.replace(':uniqueId', uniqueID), { state: { olderSearchParams: location.search } })
    }

    return <>
       { clientsToDisplay.length > 0 
            && <DynamicTableInfinite
                contentTable = {clientsToDisplay}
                contentSort = {sortType}
                valueInitSort = "name"
                index = 'uniqueId'
                handleClick = {onSelectClient}
                fetchData={fetchData}
                hasMore={hasMore}
                setSortedContent={setSortedClients}
                sortedContent={sortedClients}
                sortState={[shouldSort, setShouldSort]}
            withParams={true}
                tableName="clientsTable"
                filename="etablissements" />
        }
        {modalComponent}
    </>;
}

export default ClientsTableComponent